<template>
  <v-layout :title="title">
    <div>
      <vue-element-loading :active="$checkingAuthState" />
    </div>
    <ProductList
      v-if="!$checkingAuthState"
      :categories="categories"
      :groupedCategories="groupedCategories"
      :selectedCategory="selectedCategory"
      :selectedGroupedCategory="selectedGroupedCategory"
      :brandUuid="brandUuid"
      :drugSource="drugSource"
      :brandSource="brandSource"
    ></ProductList>
  </v-layout>
</template>


<script>
import { mapGetters, mapState } from "vuex";
import VLayout from "@/layouts/Default";
import ProductList from "@/components/ProductList";

export default {
  components: {
    VLayout,
    ProductList,
  },
  props: {
    categoryUuid: {
      default: undefined,
      type: String,
    },
    groupedCategoryUuid: {
      default: undefined,
      type: String,
    },
    brandUuid: {
      default: "",
      type: String,
    },
  },
  data() {
    return {
      // Listados predeterminados
      // Cada uno de estos listados necesita tener 'department' o 'groupedCategories'
      // para que el selector de categorías funcione correctamente
      // El caso de 'AE' es un caso especial
      listTypes: {
        ae: {
          titleLangKey: "product_list.hight_specialty_drugs",
          drugSource: "drugs/REQUEST_PHDRUGS",
          brandSource: "drugs/REQUEST_PHDRUGS_BRANDS",
        },
        topae: {
          department: "brand",
          titleLangKey: "product_list.top_specialty_drugs",
          drugSource: "drugs/REQUEST_DRUGS_TOP_SPECIALITY",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_TOP_SPECIALITY",
        },
        topfarma: {
          department: "brand",
          titleLangKey: "product_list.top_medications",
          drugSource: "drugs/REQUEST_DRUGS_TOP",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_TOP",
        },
        topderma: {
          department: "brand",
          titleLangKey: "product_list.top_derma",
          drugSource: "drugs/REQUEST_DRUGS_TOP_DERMA",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_TOP",
        },
        "offers-brand": {
          department: "brand",
          drugSource: "drugs/REQUEST_DRUGS_OFFERS",
        },
        "offers-health": {
          department: "health",
          drugSource: "drugs/REQUEST_DRUGS_OFFERS",
        },
        topgeneric: {
          department: "generic",
          titleLangKey: "product_list.top_generic",
          drugSource: "drugs/REQUEST_PHDRUGS_TOP_GENERIC",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_TOP_GENERIC",
        },
        generic_packs_offers: {
          titleLangKey: "product_list.generic_packs_offers",
          drugSource: "drugs/REQUEST_GENERIC_PACKS_OFFERS",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_PACK_OFFERS",
          groupedCategories: true, // los productos mostrados proceden de varias categorías
        },
        offers: {
          titleLangKey: "product_list.offers",
          drugSource: "drugs/REQUEST_DRUGS_OFFERS",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_OFFERS",
          groupedCategories: true, // los productos mostrados proceden de varias categorías
        },
        bestseller: {
          titleLangKey: "product_list.bestseller",
          drugSource: "drugs/REQUEST_DRUGS_BEST_SELLER",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_BEST_SELLERS",
          groupedCategories: true, // los productos mostrados proceden de varias categorías
        },
        newproducts: {
          titleLangKey: "product_list.newproducts",
          drugSource: "drugs/REQUEST_DRUGS_NEW_PRODUCTS",
          brandSource: "drugs/REQUEST_DRUGS_BRANDS_NEWS",
          groupedCategories: true, // los productos mostrados proceden de varias categorías
        },
        "offers-material": {
          department: "material",
          drugSource: "drugs/REQUEST_DRUGS_OFFERS",
        },
      },
    };
  },
  computed: {
    ...mapGetters("drugs", [
      "getAeUuid",
      "getCategoryByDepartment",
      "getCategoryByUuid",
      "getGroupedCategoryByUuid",
    ]),
    ...mapState({
      _groupedCategories: state => state.drugs.groupedCategories,
    }),
    listType() {
      return this.listTypes[this.$route.params.listType];
    },
    drugSource() {
      return (this.listType || {}).drugSource;
    },
    brandSource() {
      return (this.listType || {}).brandSource;
    },
    titleLangKey() {
      return (this.listType || {}).titleLangKey;
    },
    title() {
      if (this.titleLangKey) {
        return this.$t(this.titleLangKey);
      }
      var category = this.selectedCategory;
      return category ? this.$tradukojTranslate(category.name) : "";
    },
    rootCategory() {
      // Buscamos la categoría raíz del listado, nos servirá para saber qué
      // subcategorías mostrar

      // si listType está seteado y además es 'ae', estamos en la categoría
      // de alta especialidad
      if (this.$route.params.listType && this.$route.params.listType == "ae") {
        return this.getCategoryByUuid(this.getAeUuid);
      }

      // Si estamos en un listado predeterminado con departamento, buscamos la
      // categoría del departamento
      if (this.listType) {
        return (
          this.listType.department &&
          this.getCategoryByDepartment(this.listType.department)
        );
      }

      // En caso contrario, en la ruta nos pueden estar pasando un departamento o un
      // uuid de una categoría
      if (this.$route.params.listType) {
        return (
          this.getCategoryByUuid(this.$route.params.listType) ||
          this.getCategoryByDepartment(this.$route.params.listType)
        );
      }

      return undefined;
    },
    categories() {
      return this.rootCategory && this.rootCategory.subcategories;
    },
    groupedCategories() {
      if ((this.listType || {}).groupedCategories) {
        return this._groupedCategories;
      }
      return undefined;
    },
    drug_category_uuid: function() {
      // si dcUuid ya tiene valor (probablemente desde router/index.js) lo
      // devolvemos tal cual.
      if (this.categoryUuid) {
        return this.categoryUuid;
      }

      // si listType está seteado y además es 'ae', estamos en la categoría
      // de alta especialidad, obtenemos el uuid de la categoría y lo devolvemos
      if (this.$route.params.listType && this.$route.params.listType == "ae") {
        return this.getAeUuid;
      }

      // en cualquier otro caso, undefined.
      return undefined;
    },
    selectedCategory() {
      if (this.drug_category_uuid) {
        return this.getCategoryByUuid(this.drug_category_uuid);
      }
      return undefined;
    },
    selectedGroupedCategory() {
      if (this.groupedCategoryUuid) {
        return this.getGroupedCategoryByUuid(this.groupedCategoryUuid);
      }
      return undefined;
    },
  },
  watch: {
    $route() {
      this.loadBreadcrumb();
    },
  },
  mounted() {
    this.loadBreadcrumb();
  },
  methods: {
    loadBreadcrumb() {
      var breadcrumb = this.makeCategoriesBreadcrumb(this.selectedCategory);
      breadcrumb = this.makeCategoriesBreadcrumb(
        this.selectedGroupedCategory,
        breadcrumb
      );
      var rootTitle = this.titleLangKey || (this.rootCategory || {}).name;
      if (rootTitle) {
        breadcrumb.push({
          langkey: rootTitle,
          route: this.$route.name,
        });
      }
      breadcrumb.push({ langkey: "home.breadcrumb", route: "home" });
      breadcrumb.reverse();
      try {
        this.$store.dispatch("siteconf/CHANGE_BREADCRUMB", breadcrumb);
      } catch (err) {
        this.$sentry({ capture: err });
      }
    },
    makeCategoriesBreadcrumb(category, breadcrumb = []) {
      if (category && category.uuid !== (this.rootCategory || {}).uuid) {
        breadcrumb.push({ langkey: category.name, route: this.$route.name });
        if (category.parent != null) {
          return this.makeCategoriesBreadcrumb(category.parent, breadcrumb);
        }
      }
      return breadcrumb;
    },
  },
  fallbackI18n: {
    messages: {
      und: {
        product_list: {
          hight_specialty_drugs: "Hight Specialty Drugs",
          top_specialty_drugs: "Top Specialty Drugs",
          top_medications: "Top Medications",
          top_derma: "Top dermatology",
          offers: "Offers",
          bestseller: "Best Seller",
          newproducts: "New Products",
          generic_packs_offers: "Generic Packs Offers",
          top_generic: "Top Generic Medications",
        },
      },
    },
  },
};
</script>
